import { useEffect, useState } from 'react';

import { MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ArbitrageSlider from '@/components/ArbitrageSlider/ArbitrageSlider';
import ButtonOperation from '@/components/ButtonOperation/ButtonOperation';
import { GoBackArrowBtn } from '@/components/GoBackArrowBtn/GoBackArrowBtn';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTE } from '@/constants/route';
import { URLS } from '@/constants/urls';
import { useAuth } from '@/hooks/useAuth';
import { useListingContrats } from '@/hooks/useListingContrats';
import { useListingContratsCount } from '@/hooks/useListingContratsCount';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { ContentModalVersementLivret } from '@/modals/ContentModalVersementLivret/ContentModalVersementLivret';

import styles from './styles.module.css';

export const Operations: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, isLoading, isError } = useListingContratsCount();
  const { data: listContratData, isError: listContratError } = useListingContrats();
  const navigate = useNavigate();
  const { windowWidth } = useScreenSize();

  const [showModalVersementLivret, setShowModalVersementLivret] = useState(false);

  let contratCount: number;
  const breadcrumbItems = [{ label: 'Mes opérations', path: ROUTE.OPERATIONS }];

  const openModal = (modal: string) => {
    document.body.classList.add('modal-open');

    if (modal === 'showModalVersementLivret') {
      setShowModalVersementLivret(true);
    }
  };

  const closeModal = () => {
    setShowModalVersementLivret(false);
  };

  const handleClick = (buttonClicked: string) => {
    contratCount = (data?.ASSURANCEVIE ?? 0) + (data?.LIVRET ?? 0) + (data?.RETRAITE ?? 0);
    switch (buttonClicked) {
      case 'versement':
        void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
          event_id: 'clic',
          event_categorie: 'clic',
          event_metier: 'espace client placement',
          event_nom: 'faire un versement',
          event_detail_1: 'cta',
        });

        if (contratCount === 1 && data?.LIVRET === 1) {
          openModal('showModalVersementLivret');
        } else if (contratCount === 1) {
          navigate(ROUTE.SELECTOPERATION, {
            state: {
              key: 'versement',
              breadcrumbItems: breadcrumbItems,
              infoContrat: listContratData?.listContrat?.[0],
            },
          });
        } else {
          navigate(ROUTE.SELECTCONTRACT, {
            state: { key: 'versement' },
          });
        }
        break;

      case 'investir':
        void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
          event_id: 'clic',
          event_categorie: 'clic',
          event_metier: 'espace client placement',
          event_nom: 'investir',
          event_detail_1: 'cta',
        });
        window.open(URLS.MTP_URL_INVESTMENT, '_blank', 'noopener');
        break;

      case 'arbitrage':
        void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
          event_id: 'clic',
          event_categorie: 'clic',
          event_metier: 'espace client placement',
          event_nom: 'faire un arbitrage',
          event_detail_1: 'cta',
        });

        if (contratCount === 1) {
          navigate(ROUTE.SELECTOPERATION, {
            state: {
              key: 'arbitrage',
              breadcrumbItems: breadcrumbItems,
              infoContrat: listContratData?.listContrat?.[0],
            },
          });
        } else {
          navigate(ROUTE.SELECTCONTRACT, {
            state: { key: 'arbitrage' },
          });
        }
        break;

      case 'rachat/retrait':
        void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
          event_id: 'clic',
          event_categorie: 'clic',
          event_metier: 'espace client placement',
          event_nom: 'faire un rachat/ retrait',
          event_detail_1: 'cta',
        });

        if (contratCount === 1) {
          navigate(ROUTE.SELECTOPERATION, {
            state: {
              key: 'rachat/retrait',
              breadcrumbItems: breadcrumbItems,
              infoContrat: listContratData?.listContrat?.[0],
            },
          });
        } else {
          navigate(ROUTE.SELECTCONTRACT, {
            state: { key: 'rachat/retrait' },
          });
        }
    }
  };

  if (sessionStorage.parcoursVersementData) {
    sessionStorage.removeItem('parcoursVersementData');
  }

  useEffect(() => {
    if (user?.id && user.type && user.success) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: 'mes operations',
        page_template: 'espace client',
        user_id: user.id,
        user_client: user.type === 'Client',
        user_connexion: user?.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: 'mes operations',
        event_page: 'mes operations',
      });
    }
  }, [user?.id, user?.type, user?.success]);

  if (isLoading) {
    return <Loader />;
  }
  if (isError || listContratError) navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.DASHBOARD } });
  return (
    <>
      {isLoading && <Loader />}

      {showModalVersementLivret && (
        <ModaleDefaut
          title="Le versement s’effectue depuis votre compte de confiance"
          content={ContentModalVersementLivret}
          btnLabel="Retour à l'accueil"
          btnVariant="primary"
          btnSize="large"
          onClickBtn={() => {
            document.body.classList.remove('modal-open');
            navigate(ROUTE.DASHBOARD);
          }}
          btnFullwidth={false}
          onClickClose={() => closeModal()}
          mobileStyle="stick-to-bottom"
          size="large"
          infoCircle={true}
        />
      )}
      {windowWidth < 900 && <GoBackArrowBtn />}
      <MtxGrid container fullWidth className={styles.boxOperations} row>
        <div className={styles.infoSection}>
          <div className={styles.contentTitle}>
            <h1>{t('operations.title')}</h1>
          </div>
          {FEATURE_FLAGS.ARBITRAGE && <ArbitrageSlider className={styles.proposalSliderWrapper} showTitle />}
        </div>

        <h2 className={styles.optionsTitle}>{t('operations.subTitle')}</h2>

        <MtxGrid className={styles.operationLinks}>
          {((data?.ASSURANCEVIE ?? 0) > 0 || (data?.LIVRET ?? 0) > 0 || (data?.RETRAITE ?? 0) > 0) && (
            <MtxGrid row>
              <MtxGrid col={5} colXs={12} colSm={8} colMd={5} colLg={5} colXl={5}>
                <ButtonOperation
                  icon="ICON_EFFECTUER_VERSEMENT"
                  iconFleche="ICON_FLECHE"
                  sousTitre="SOUS_TITRE_VERSEMENT"
                  titre="TITRE_VERSEMENT"
                  onClick={() => {
                    handleClick('versement');
                  }}
                />
              </MtxGrid>
            </MtxGrid>
          )}

          <MtxGrid row>
            <MtxGrid col={5} colXs={12} colSm={8} colMd={5} colLg={5} colXl={5}>
              <ButtonOperation
                icon="ICON_INVESTIR"
                iconFleche="ICON_FLECHE"
                sousTitre="SOUS_TITRE_INVESTIR"
                titre="TITRE_INVESTIR"
                onClick={() => {
                  handleClick('investir');
                }}
              />
            </MtxGrid>
          </MtxGrid>
          {((data?.ASSURANCEVIE ?? 0) > 0 || (data?.RETRAITE ?? 0) > 0) && (
            <MtxGrid row>
              <MtxGrid col={5} colXs={12} colSm={8} colMd={5} colLg={5} colXl={5}>
                <ButtonOperation
                  icon="ICON_ARBITRAGE"
                  iconFleche="ICON_FLECHE"
                  sousTitre="SOUS_TITRE_ARBITRAGE"
                  titre="TITRE_ARBITRAGE"
                  onClick={() => {
                    handleClick('arbitrage');
                  }}
                />
              </MtxGrid>
            </MtxGrid>
          )}
          {((data?.ASSURANCEVIE ?? 0) > 0 || (data?.LIVRET ?? 0) > 0 || (data?.RETRAITE ?? 0) > 0) && (
            <MtxGrid row>
              <MtxGrid col={5} colXs={12} colSm={8} colMd={5} colLg={5} colXl={5}>
                <ButtonOperation
                  icon="ICON_RACHAT_RETRAIT"
                  iconFleche="ICON_FLECHE"
                  sousTitre="SOUS_TITRE_RACHAT_RETRAIT"
                  titre="TITRE_RACHAT_RETRAIT"
                  onClick={() => {
                    handleClick('rachat/retrait');
                  }}
                />
              </MtxGrid>
            </MtxGrid>
          )}
        </MtxGrid>
      </MtxGrid>
    </>
  );
};
